import { format } from 'date-fns-tz';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Controller, useFormContext } from 'react-hook-form';

import { faPlus } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    Box,
    Button,
    Collapse,
    Divider,
    FormControl,
    FormHelperText,
    MenuItem,
    Slider,
    Stack,
    TextField,
    Typography,
    TypographyProps,
    useTheme,
} from '@mui/material';
import { Select } from '@sage/ui-components';

import { useAnalytics } from 'src/AnalyticsContext';
import { ResidentCareFormValues } from 'src/hooks/forms/useResidentCareTaskForm';
import { CareTask, OutcomeType } from 'src/store/sageAdminApi';
import {
    ChartType,
    buildPlannedCareFormFields,
    convertStartAndEndTimeToMs,
    getOutcomeForChartingReason,
} from 'src/utils/residentCareFormUtils';

interface PlannedCareCardFormProps {
    // ID used to search up form state for this care task
    taskFormId: string;
    careTask: CareTask;
    chartType: ChartType;
    shouldRestrictTimeFields?: boolean;
    trackingFields: {
        residentId?: string;
        careTaskId: string;
    };
}

const FormFieldLabel = ({
    required,
    htmlFor,
    children,
    ...props
}: { required?: boolean; htmlFor: string; children: React.ReactNode } & Omit<
    TypographyProps<'label'>,
    'component'
>) => (
    <Typography
        component="label"
        htmlFor={htmlFor}
        variant="body2"
        sx={{ fontWeight: 'bold' }}
        {...props}
    >
        {children}
        {required && (
            <Typography component="span" sx={{ color: 'error.main' }}>
                {' '}
                *
            </Typography>
        )}
    </Typography>
);

type IncompleteOptions = Exclude<OutcomeType, 'COMPLETED'>;
const IncompleteReasonSelectOptions: { id: IncompleteOptions; name: string }[] = [
    { id: 'REFUSED', name: 'Refused' },
    { id: 'NOT_COMPLETED', name: 'Other' },
];
const MinuteInMs = 60000;
const preventTouchDefault = (e: Event) => e.preventDefault();

// This expects a FromProvider with useResidentCareForm
// State is maintained by the form. Field changes are sent directly to the form
const PlannedCareCardFormFragment = ({
    taskFormId,
    careTask,
    chartType,
    shouldRestrictTimeFields = false,
    trackingFields,
}: PlannedCareCardFormProps) => {
    const { spacing } = useTheme();
    const {
        control,
        register,
        formState: { errors: errorsFromForm },
        setValue,
        getValues,
    } = useFormContext<ResidentCareFormValues>();

    const {
        showIncompleteReasonButtons,
        supportedChartingReasons = [],
        supportedCustomOutcomes = [],
        startTimeField,
        endTimeField,
        durationField,
        noteField,
        reasonField,
    } = useMemo(
        () => buildPlannedCareFormFields(careTask, chartType, shouldRestrictTimeFields),
        [chartType, careTask, shouldRestrictTimeFields]
    );

    const { trackEvent } = useAnalytics();

    const errors = errorsFromForm.plannedCare?.[taskFormId] ?? {};

    const [isAddNoteButtonClicked, setIsAddNoteButtonClicked] = useState<boolean>(false);
    const [durationSliderDragValue, setDurationSliderDragValue] = useState<number | undefined>();

    // useEvent from react-use isn't suitable here because it reads the target at render time,
    // when the ref is still null. useEffect runs after React populates the ref, so the
    // listeners are attached to the actual element with no extra render cycle.
    // This workaround is included to address MUI slider behavior on iOS specifically. Github issue can be
    // found here: https://github.com/mui/material-ui/pull/48164
    const sliderRef = useRef<HTMLSpanElement>(null);
    useEffect(() => {
        const sliderEl = sliderRef.current;
        if (!sliderEl) return;
        sliderEl.addEventListener('touchstart', preventTouchDefault, { passive: false });
        sliderEl.addEventListener('touchmove', preventTouchDefault, { passive: false });
        return () => {
            sliderEl.removeEventListener('touchstart', preventTouchDefault);
            sliderEl.removeEventListener('touchmove', preventTouchDefault);
        };
    }, []);

    const isNoteTextFieldExpanded = isAddNoteButtonClicked || noteField.isRequired;
    const showCustomOutcomeSelector = supportedCustomOutcomes.length > 0;

    const showChartingReasonDropdown = reasonField.isShown && supportedChartingReasons.length > 0;
    const reasonLabel = chartType === 'incomplete' ? 'Incomplete Reason' : 'Reason';

    const getFieldId = (field: string) => `${taskFormId}-${field}`;

    const trackFieldEvent = (eventName: string, fieldSelected: string) => {
        trackEvent(eventName, {
            pageName: 'Log Care',
            residentId: trackingFields.residentId,
            careTaskId: trackingFields.careTaskId,
            fieldSelected,
        });
    };

    const allTimeFieldsVisible =
        startTimeField.isShown && endTimeField.isShown && durationField.isShown;

    const syncDuration = () => {
        if (!allTimeFieldsVisible) return;
        const { startTime, endTime } = getValues(`plannedCare.${taskFormId}`);
        const { startTimeMs, endTimeMs } = convertStartAndEndTimeToMs(startTime, endTime);
        if (!startTimeMs || !endTimeMs) return;

        setValue(
            `plannedCare.${taskFormId}.durationInMinutes`,
            Math.floor((endTimeMs - startTimeMs) / MinuteInMs)
        );
    };

    const syncStartEndTime = (newDuration: number) => {
        // Duration could be 0, in which case we don't sync
        if (!allTimeFieldsVisible || !newDuration) return;
        const { startTime, endTime } = getValues(`plannedCare.${taskFormId}`);
        const { startTimeMs, endTimeMs } = convertStartAndEndTimeToMs(startTime, endTime);
        // If there is a startTime, we update endTime. If there is no startTime, but there is an endTime, update startTime.
        if (startTimeMs) {
            setValue(
                `plannedCare.${taskFormId}.endTime`,
                format(startTimeMs + newDuration * MinuteInMs, 'HH:mm')
            );
        } else if (endTimeMs) {
            setValue(
                `plannedCare.${taskFormId}.startTime`,
                format(endTimeMs - newDuration * MinuteInMs, 'HH:mm')
            );
        }
    };

    const handleIncompleteReasonChange = (buttonOption: IncompleteOptions) =>
        trackFieldEvent('Incomplete Reason Selection', buttonOption);

    const handleChartingReasonChange = (reasonId: string) => {
        const reason = supportedChartingReasons.find((r) => r.id === reasonId);
        setValue(
            `plannedCare.${taskFormId}.outcome`,
            getOutcomeForChartingReason(reason, chartType)
        );
        trackFieldEvent('Charting Reason Selection', reasonId);
    };

    const handleCustomOutcomeChange = (value: string) =>
        trackFieldEvent('Custom Outcome Selection', value);

    /*
     * The same dropdown serves two shapes. When the task supplies its own reasons we chart
     * one of those; otherwise it falls back to picking a bare Outcome. Only the bound field,
     * the options and what a selection implies differ.
     */
    const reasonSelect = showChartingReasonDropdown
        ? {
              fieldName: `plannedCare.${taskFormId}.chartedReasonId` as const,
              label: reasonLabel,
              isRequired: reasonField.isRequired,
              rules: reasonField.isRequired ? { required: `${reasonLabel} is required` } : {},
              options: supportedChartingReasons.flatMap((reason) =>
                  reason.id ? { id: reason.id, name: reason.label || reason.id } : []
              ),
              onSelected: handleChartingReasonChange,
          }
        : {
              fieldName: `plannedCare.${taskFormId}.outcome` as const,
              label: 'Incomplete Reason',
              isRequired: true,
              rules: { required: 'Incomplete reason is required' },
              options: IncompleteReasonSelectOptions,
              onSelected: (selected: string) =>
                  handleIncompleteReasonChange(selected as IncompleteOptions),
          };

    const handleDurationSliderChangeCommitted = (newDuration: number) => {
        setDurationSliderDragValue(undefined);
        syncStartEndTime(newDuration);
        trackFieldEvent('Duration Slider Dragged', 'durationInMinutes');
    };

    const handleDurationInputBlur = () => trackFieldEvent('Duration Input', 'durationInMinutes');

    const handleStartTimeBlur = () => {
        syncDuration();
        trackFieldEvent('Start/End Time Input', 'startTime');
    };

    const handleEndTimeBlur = () => {
        syncDuration();
        trackFieldEvent('Start/End Time Input', 'endTime');
    };

    const handleNoteBlur = () => trackFieldEvent('Note Input', 'note');

    const hasVisibleFormContent = [
        showIncompleteReasonButtons,
        showChartingReasonDropdown,
        startTimeField.isShown,
        endTimeField.isShown,
        durationField.isShown,
        noteField.isShown,
        showCustomOutcomeSelector,
    ].some(Boolean);

    if (!hasVisibleFormContent) return null;

    return (
        <>
            <Divider />
            <Box sx={{ p: 2 }}>
                <Stack sx={{ gap: 3 }}>
                    {(showChartingReasonDropdown || showIncompleteReasonButtons) && (
                        <Stack sx={{ gap: 1.5 }}>
                            <Controller
                                name={reasonSelect.fieldName}
                                control={control}
                                rules={reasonSelect.rules}
                                render={({
                                    field: { value, onChange, ref },
                                    fieldState: { error },
                                }) => (
                                    <FormControl fullWidth error={!!error}>
                                        <Stack sx={{ gap: 0.5 }}>
                                            <FormFieldLabel
                                                htmlFor={getFieldId('incomplete-reason')}
                                                required={reasonSelect.isRequired}
                                            >
                                                {reasonSelect.label}
                                            </FormFieldLabel>
                                            <Select
                                                id={getFieldId('incomplete-reason')}
                                                labelId={`${getFieldId('incomplete-reason')}-label`}
                                                variant="outlined"
                                                value={value || ''}
                                                onChange={(e) => {
                                                    const selected = e.target.value as string;
                                                    onChange(selected);
                                                    reasonSelect.onSelected(selected);
                                                }}
                                                inputRef={ref}
                                                error={!!error}
                                                sx={{
                                                    borderRadius: 3,
                                                }}
                                            >
                                                {reasonSelect.options.map((option) => (
                                                    <MenuItem
                                                        key={`${getFieldId(
                                                            'incomplete-reason'
                                                        )}-reason-${option.id}`}
                                                        value={option.id}
                                                    >
                                                        {option.name}
                                                    </MenuItem>
                                                ))}
                                            </Select>
                                            {error && (
                                                <FormHelperText error>
                                                    {error.message}
                                                </FormHelperText>
                                            )}
                                        </Stack>
                                    </FormControl>
                                )}
                            />
                        </Stack>
                    )}
                    {(startTimeField.isShown || endTimeField.isShown) && (
                        <Stack direction="row" sx={{ gap: 2, justifyContent: 'space-between' }}>
                            <>
                                {startTimeField.isShown && (
                                    <Stack sx={{ gap: 1, flex: 1 }}>
                                        <FormFieldLabel
                                            htmlFor={getFieldId('start-time')}
                                            required={startTimeField.isRequired}
                                        >
                                            Start Time
                                        </FormFieldLabel>
                                        <TextField
                                            id={getFieldId('start-time')}
                                            type="time"
                                            {...register(`plannedCare.${taskFormId}.startTime`, {
                                                required:
                                                    startTimeField.isRequired &&
                                                    'Start time is required',
                                            })}
                                            onBlur={handleStartTimeBlur}
                                            error={!!errors.startTime}
                                            helperText={errors.startTime?.message}
                                            sx={{
                                                '& .MuiOutlinedInput-root': {
                                                    borderRadius: 3,
                                                },
                                            }}
                                        />
                                    </Stack>
                                )}
                                {endTimeField.isShown && (
                                    <Stack sx={{ gap: 1, flex: 1 }}>
                                        <FormFieldLabel
                                            htmlFor={getFieldId('end-time')}
                                            required={endTimeField.isRequired}
                                        >
                                            End Time
                                        </FormFieldLabel>
                                        <TextField
                                            id={getFieldId('end-time')}
                                            type="time"
                                            {...register(`plannedCare.${taskFormId}.endTime`, {
                                                required:
                                                    endTimeField.isRequired &&
                                                    'End time is required',
                                            })}
                                            onBlur={handleEndTimeBlur}
                                            error={!!errors.endTime}
                                            helperText={errors.endTime?.message}
                                            sx={{
                                                '& .MuiOutlinedInput-root': {
                                                    borderRadius: 3,
                                                },
                                            }}
                                        />
                                    </Stack>
                                )}
                            </>
                        </Stack>
                    )}
                    {durationField.isShown && (
                        <Stack sx={{ gap: 1.5 }}>
                            <FormFieldLabel
                                htmlFor={getFieldId('duration')}
                                required={durationField.isRequired}
                            >
                                Duration
                                <Typography
                                    variant="body2"
                                    sx={{ fontWeight: 'medium' }}
                                    component="span"
                                >
                                    {' '}
                                    (Minutes)
                                </Typography>
                            </FormFieldLabel>
                            <Controller
                                name={`plannedCare.${taskFormId}.durationInMinutes`}
                                control={control}
                                rules={{
                                    validate: durationField.isRequired
                                        ? (value) => (value && value >= 1) || 'Duration is required'
                                        : undefined,
                                    max: {
                                        value: 999,
                                        message: 'Duration cannot be more than 999 minutes',
                                    },
                                }}
                                render={({ field: { value, onChange }, fieldState: { error } }) => (
                                    <Stack>
                                        <Stack
                                            direction="row"
                                            sx={{
                                                alignItems: 'center',
                                                gap: 3.5,
                                            }}
                                        >
                                            <Slider
                                                ref={sliderRef}
                                                value={durationSliderDragValue ?? value ?? 0}
                                                onChange={(_e, newValue) => {
                                                    setDurationSliderDragValue(
                                                        Math.round(newValue)
                                                    );
                                                }}
                                                onChangeCommitted={(_e, newValue) => {
                                                    const roundedValue = Math.round(newValue);
                                                    handleDurationSliderChangeCommitted(
                                                        roundedValue
                                                    );
                                                    onChange(roundedValue);
                                                }}
                                                valueLabelDisplay="auto"
                                                valueLabelFormat={(val) => Math.round(val)}
                                                min={0}
                                                max={60}
                                                sx={{
                                                    touchAction: 'none',
                                                    // Slider will be replaced by a custom component
                                                    '& .MuiSlider-valueLabel': {
                                                        backgroundColor: 'primary.main',
                                                    },
                                                    '& .MuiSlider-thumb': {
                                                        width: 36, // But, why not make it look ok for now
                                                        height: 36,
                                                        ml: 1,
                                                    },
                                                    '& .MuiSlider-track': {
                                                        height: 16,
                                                    },
                                                    '& .MuiSlider-rail': {
                                                        height: 16,
                                                    },
                                                }}
                                            />
                                            <TextField
                                                id={getFieldId('duration')}
                                                value={value || ''}
                                                onChange={(e) => {
                                                    const newValue = Math.max(
                                                        0,
                                                        Number(e.target.value)
                                                    );
                                                    syncStartEndTime(newValue);
                                                    onChange(newValue);
                                                }}
                                                onBlur={handleDurationInputBlur}
                                                error={!!error}
                                                type="number" // We should move away from this https://technology.blog.gov.uk/2020/02/24/why-the-gov-uk-design-system-team-changed-the-input-type-for-numbers/
                                                slotProps={{
                                                    // Move to https://mui.com/material-ui/react-number-field/
                                                    input: {
                                                        sx: {
                                                            borderRadius: 3,
                                                            height: spacing(5),
                                                            minWidth: spacing(8),
                                                            maxWidth: spacing(10),
                                                        },
                                                    },
                                                }}
                                            />
                                        </Stack>
                                        {error && (
                                            <FormHelperText error>{error.message}</FormHelperText>
                                        )}
                                    </Stack>
                                )}
                            />
                        </Stack>
                    )}
                    {showCustomOutcomeSelector && (
                        <Stack sx={{ gap: 1.5 }}>
                            <Controller
                                name={`plannedCare.${taskFormId}.chartedCustomOutcomeId`}
                                control={control}
                                rules={{
                                    required: 'Custom outcome is required',
                                }}
                                render={({
                                    field: { value, onChange, ref },
                                    fieldState: { error },
                                }) => (
                                    <FormControl fullWidth error={!!error}>
                                        <Stack sx={{ gap: 0.5 }}>
                                            <FormFieldLabel
                                                htmlFor={getFieldId('custom-outcome')}
                                                required
                                            >
                                                Custom Outcome
                                            </FormFieldLabel>
                                            <Select
                                                id={getFieldId('custom-outcome')}
                                                labelId={`${getFieldId('custom-outcome')}-label`}
                                                variant="outlined"
                                                value={value || ''}
                                                onChange={(e) => {
                                                    onChange(e.target.value);
                                                    handleCustomOutcomeChange(
                                                        e.target.value as string
                                                    );
                                                }}
                                                inputRef={ref}
                                                error={!!error}
                                                sx={{
                                                    borderRadius: 3,
                                                }}
                                            >
                                                {supportedCustomOutcomes?.map((outcome) => (
                                                    <MenuItem
                                                        key={`${getFieldId(
                                                            'custom-outcome'
                                                        )}-outcome-${outcome.id}`}
                                                        value={outcome.id}
                                                    >
                                                        {outcome.name}
                                                    </MenuItem>
                                                ))}
                                            </Select>
                                            {error && (
                                                <FormHelperText error>
                                                    {error.message}
                                                </FormHelperText>
                                            )}
                                        </Stack>
                                    </FormControl>
                                )}
                            />
                        </Stack>
                    )}
                    {noteField.isShown && (
                        <Box>
                            <Stack
                                direction="row"
                                sx={{ justifyContent: 'space-between', alignItems: 'center' }}
                            >
                                <FormFieldLabel
                                    htmlFor={getFieldId('note')}
                                    required={noteField.isRequired}
                                >
                                    Note
                                </FormFieldLabel>
                                {!isNoteTextFieldExpanded && (
                                    <Button
                                        variant="text"
                                        startIcon={<FontAwesomeIcon icon={faPlus} />}
                                        onClick={() => setIsAddNoteButtonClicked(true)}
                                        size="extraSmall"
                                        sx={{ p: 0 }}
                                    >
                                        Add note
                                    </Button>
                                )}
                            </Stack>
                            <Collapse in={isNoteTextFieldExpanded} sx={{ pt: 1 }}>
                                <TextField
                                    id={getFieldId('note')}
                                    {...register(`plannedCare.${taskFormId}.note`, {
                                        required:
                                            noteField.isRequired &&
                                            'A note is required for this Task',
                                    })}
                                    onBlur={handleNoteBlur}
                                    error={!!errors.note}
                                    helperText={errors.note?.message}
                                    placeholder="Add note here"
                                    multiline
                                    minRows={2}
                                    fullWidth
                                    variant="outlined"
                                    sx={{
                                        '& .MuiOutlinedInput-root': {
                                            borderRadius: 3,
                                        },
                                    }}
                                />
                            </Collapse>
                        </Box>
                    )}
                </Stack>
            </Box>
        </>
    );
};

export default PlannedCareCardFormFragment;
